/**
 * TabFormModal — F12.N Etap 4 E4.T2/T3/T4.
 *
 * Modal dodawania nowej zakładki user-added w buildery systemowym
 * (`SystemModuleBuilderPage`) lub edycji istniejącej user-added.
 * Trzy pola: title (required, max 50), slug (auto-gen z title, edytowalny,
 * unique w obrębie layoutu) + icon (z IconPicker, ~80 ikon Lucide).
 *
 * Walidacja:
 *  - title: niepusty, max 50 znaków
 *  - slug: pasuje `^tab-[a-z0-9-]{1,30}$` (prefix `tab-` zachowany dla
 *    odróżnienia user-added od systemowych — heurystyka z F12.N D3.3)
 *  - slug: unique w `existingSlugs`
 *
 * Mode:
 *  - 'add' — pusty formularz, slug auto-syncuje się z title
 *  - 'edit' — pre-fill z `initial`, slug nie syncuje się automatycznie
 *    (chronimy ręczne edycje admina)
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { IconPicker } from '@/components/modules/IconPicker';
import { RadioGroup } from '@/components/RadioGroup';

/** Typ nowo tworzonej zakładki — wybór dostępny TYLKO w trybie 'add' (§patrz `NewTabKind`). */
export type NewTabKind = 'fields' | 'additional_query' | 'additional';

export interface TabFormValues {
  title: string;
  slug: string;
  icon?: string;
  /**
   * Typ nowo tworzonej zakładki. Obecny TYLKO gdy `mode === 'add'` (edycja nie
   * zmienia typu istniejącej zakładki). Dla 'additional_query' (F22, widoki
   * dodatkowe) i 'additional' (tabele dodatkowe) typ jest ustalany raz przy
   * tworzeniu, a dalej edytowalny jest tylko WYBÓR celu — widoku/tabeli —
   * przez panel konfiguracji w canvasie, nie przez ten modal.
   * Brak klucza = 'fields' (dotychczasowe zachowanie, zgodność wsteczna).
   */
  tabType?: NewTabKind;
}

interface TabFormModalProps {
  open: boolean;
  mode: 'add' | 'edit';
  /** Tytuł nagłówka modal'a — domyślnie tłumaczenie z i18n per mode. */
  initial?: Partial<TabFormValues>;
  /** Slugi już istniejące w layoutie — używane do walidacji unique.
      Powinno zawierać wszystkie systemowe (data/contacts/...) + user-added.
      W trybie 'edit' — bez slug edytowanej zakładki (żeby self-match się nie zaliczał). */
  existingSlugs: string[];
  onSubmit: (values: TabFormValues) => void;
  onClose: () => void;
}

const TITLE_MAX = 50;
const SLUG_PREFIX = 'tab-';
const SLUG_BODY_MAX = 30; // bez prefiksu
const SLUG_BODY_RE = /^[a-z0-9-]+$/;

/**
 * Zamienia title na slug body (bez prefiksu `tab-`). Polskie znaki transliterowane.
 * Przykład: „Faktury VAT" → „faktury-vat".
 */
function slugifyTitle(title: string): string {
  const normalized = title
    .toLowerCase()
    .replace(/ą/g, 'a')
    .replace(/ć/g, 'c')
    .replace(/ę/g, 'e')
    .replace(/ł/g, 'l')
    .replace(/ń/g, 'n')
    .replace(/ó/g, 'o')
    .replace(/ś/g, 's')
    .replace(/ź|ż/g, 'z')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized.slice(0, SLUG_BODY_MAX);
}

export function TabFormModal({
  open,
  mode,
  initial,
  existingSlugs,
  onSubmit,
  onClose,
}: TabFormModalProps) {
  const { t } = useTranslation();

  const [title, setTitle] = useState('');
  const [slugBody, setSlugBody] = useState(''); // bez prefiksu `tab-`
  const [icon, setIcon] = useState<string | undefined>(undefined);
  const [tabType, setTabType] = useState<NewTabKind>('fields');
  const [slugAutoSync, setSlugAutoSync] = useState(true);
  const [errors, setErrors] = useState<Partial<Record<keyof TabFormValues, string>>>({});

  // Reset przy otwarciu
  useEffect(() => {
    if (!open) return;
    const initialTitle = initial?.title ?? '';
    const initialSlugBody = (initial?.slug ?? '').replace(/^tab-/, '');
    setTitle(initialTitle);
    setSlugBody(initialSlugBody);
    setIcon(initial?.icon);
    setTabType('fields');
    // W trybie 'edit' wyłączamy auto-sync (admin mógł ręcznie edytować slug)
    setSlugAutoSync(mode === 'add');
    setErrors({});
  }, [open, mode, initial?.title, initial?.slug, initial?.icon]);

  function handleTitleChange(next: string) {
    setTitle(next);
    if (slugAutoSync) {
      setSlugBody(slugifyTitle(next));
    }
    if (errors.title) setErrors((e) => ({ ...e, title: undefined }));
  }

  function handleSlugChange(next: string) {
    setSlugAutoSync(false);
    setSlugBody(next.toLowerCase().replace(/[^a-z0-9-]/g, ''));
    if (errors.slug) setErrors((e) => ({ ...e, slug: undefined }));
  }

  function validate(): boolean {
    const next: Partial<Record<keyof TabFormValues, string>> = {};
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      next.title = t('tenant.systemModuleBuilder.tabFormModal.errorTitleRequired');
    } else if (trimmedTitle.length > TITLE_MAX) {
      next.title = t('tenant.systemModuleBuilder.tabFormModal.errorTitleTooLong', {
        max: TITLE_MAX,
      });
    }

    if (!slugBody) {
      next.slug = t('tenant.systemModuleBuilder.tabFormModal.errorSlugRequired');
    } else if (!SLUG_BODY_RE.test(slugBody)) {
      next.slug = t('tenant.systemModuleBuilder.tabFormModal.errorSlugInvalid');
    } else if (slugBody.length > SLUG_BODY_MAX) {
      next.slug = t('tenant.systemModuleBuilder.tabFormModal.errorSlugTooLong', {
        max: SLUG_BODY_MAX,
      });
    } else {
      const fullSlug = SLUG_PREFIX + slugBody;
      if (existingSlugs.includes(fullSlug)) {
        next.slug = t('tenant.systemModuleBuilder.tabFormModal.errorSlugDuplicate');
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      title: title.trim(),
      slug: SLUG_PREFIX + slugBody,
      ...(icon ? { icon } : {}),
      ...(mode === 'add' ? { tabType } : {}),
    });
  }

  const modalTitle =
    mode === 'add'
      ? t('tenant.systemModuleBuilder.tabFormModal.titleAdd')
      : t('tenant.systemModuleBuilder.tabFormModal.titleEdit');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} data-testid="tab-form-modal-cancel">
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} data-testid="tab-form-modal-submit">
            {mode === 'add'
              ? t('tenant.systemModuleBuilder.tabFormModal.submitAdd')
              : t('tenant.systemModuleBuilder.tabFormModal.submitEdit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mode === 'add' && (
          <FormField label={t('tenant.systemModuleBuilder.tabFormModal.typeLabel')}>
            <RadioGroup
              name="tab-form-modal-type"
              value={tabType}
              onChange={setTabType}
              data-testid="tab-form-modal-type"
              options={[
                {
                  value: 'fields',
                  label: t('tenant.systemModuleBuilder.tabFormModal.typeFieldsLabel'),
                  description: t('tenant.systemModuleBuilder.tabFormModal.typeFieldsDescription'),
                },
                {
                  value: 'additional_query',
                  label: t('tenant.systemModuleBuilder.tabFormModal.typeAdditionalQueryLabel'),
                  description: t(
                    'tenant.systemModuleBuilder.tabFormModal.typeAdditionalQueryDescription',
                  ),
                },
                {
                  value: 'additional',
                  label: t('tenant.systemModuleBuilder.tabFormModal.typeAdditionalTableLabel'),
                  description: t(
                    'tenant.systemModuleBuilder.tabFormModal.typeAdditionalTableDescription',
                  ),
                },
              ]}
            />
          </FormField>
        )}
        <FormField
          label={t('tenant.systemModuleBuilder.tabFormModal.titleLabel')}
          required
          error={errors.title}
        >
          <Input
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={t('tenant.systemModuleBuilder.tabFormModal.titlePlaceholder')}
            maxLength={TITLE_MAX}
            autoFocus
            data-testid="tab-form-modal-title-input"
          />
        </FormField>

        <FormField
          label={t('tenant.systemModuleBuilder.tabFormModal.slugLabel')}
          required
          error={errors.slug}
          hint={t('tenant.systemModuleBuilder.tabFormModal.slugHint')}
          htmlFor="tab-form-slug-input"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-muted">{SLUG_PREFIX}</span>
            <Input
              id="tab-form-slug-input"
              value={slugBody}
              onChange={(e) => handleSlugChange(e.target.value)}
              maxLength={SLUG_BODY_MAX}
              data-testid="tab-form-modal-slug-input"
            />
          </div>
        </FormField>

        <FormField label={t('tenant.systemModuleBuilder.tabFormModal.iconLabel')}>
          <IconPicker
            value={icon ?? ''}
            onChange={(name) => setIcon(name || undefined)}
            ariaLabel={t('tenant.systemModuleBuilder.tabFormModal.iconLabel')}
          />
        </FormField>
      </div>
    </Modal>
  );
}
